import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import editaisAdminService from "../../../services/editaisAdminService";
import type { EditalDetalhado } from "../../../services/editaisAdminService";

const STATUS_COLORS = {
  rascunho: "bg-gray-100 text-gray-800",
  publicado: "bg-green-100 text-green-800",
  encerrado: "bg-red-100 text-red-800",
};

const STATUS_LABELS = {
  rascunho: "Rascunho",
  publicado: "Publicado",
  encerrado: "Encerrado",
};

export default function DetalhesEdital() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [edital, setEdital] = useState<EditalDetalhado | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processando, setProcessando] = useState(false);
  const [uploadando, setUploadando] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [filesParaUpload, setFilesParaUpload] = useState<File[]>([]);

  const carregarEdital = async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      const response = await editaisAdminService.buscar(parseInt(id));
      console.log("Edital recebido:", response.edital);
      console.log("Template do edital:", response.edital.template);
      setEdital(response.edital);

      // Carregar estatísticas
      if (response.edital.status !== "rascunho") {
        const stats = await editaisAdminService.estatisticas(parseInt(id));
        setEdital((prev) =>
          prev ? { ...prev, estatisticas: stats.estatisticas } : null
        );
      }
    } catch (err) {
      setError("Erro ao carregar edital");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void carregarEdital();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handlePublicar = async () => {
    if (
      !id ||
      !confirm(
        "Deseja publicar este edital? Ele ficará visível para os proponentes."
      )
    )
      return;

    try {
      setProcessando(true);
      await editaisAdminService.publicar(parseInt(id));
      toast.success("Edital publicado com sucesso!");
      await carregarEdital();
    } catch (err) {
      const errorMessage =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || "Erro ao publicar edital";
      toast.error(errorMessage);
      console.error("Erro ao publicar:", err);
    } finally {
      setProcessando(false);
    }
  };

  const handleEncerrar = async () => {
    if (
      !id ||
      !confirm("Deseja encerrar este edital? Esta ação não pode ser desfeita.")
    )
      return;

    try {
      setProcessando(true);
      await editaisAdminService.encerrar(parseInt(id));
      toast.success("Edital encerrado com sucesso!");
      await carregarEdital();
    } catch (err) {
      const errorMessage =
        (err as { response?: { data?: { error?: string } } })?.response?.data
          ?.error || "Erro ao encerrar edital";
      toast.error(errorMessage);
      console.error("Erro ao encerrar:", err);
    } finally {
      setProcessando(false);
    }
  };

  const handleExcluir = async () => {
    if (
      !id ||
      !confirm(
        "Deseja excluir este edital? Esta ação não pode ser desfeita e todos os dados serão perdidos."
      )
    )
      return;

    try {
      setProcessando(true);
      await editaisAdminService.excluir(parseInt(id));
      toast.success("Edital excluído com sucesso!");
      navigate("/admin/editais");
    } catch (err) {
      toast.error("Erro ao excluir edital");
      console.error(err);
      setProcessando(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Carregando edital...</p>
        </div>
      </div>
    );
  }

  if (error || !edital) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error || "Edital não encontrado"}</p>
        </div>
        <Link
          to="/admin/editais"
          className="mt-4 inline-block text-blue-600 hover:text-blue-800"
        >
          ← Voltar para lista
        </Link>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Botão Voltar */}
      <div className="mb-4">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-600 hover:text-gray-900 transition font-medium"
        >
          ← Voltar
        </button>
      </div>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{edital.nome}</h1>
            <p className="mt-2 text-gray-600">ID: {edital.id}</p>
          </div>
          <span
            className={`px-4 py-2 inline-flex text-sm leading-5 font-semibold rounded-full ${
              STATUS_COLORS[edital.status]
            }`}
          >
            {STATUS_LABELS[edital.status]}
          </span>
        </div>
      </div>

      {/* Ações */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Ações</h2>
        <div className="flex gap-3 flex-wrap">
          {edital.status === "rascunho" && (
            <>
              <button
                onClick={handlePublicar}
                disabled={processando}
                className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition disabled:opacity-50"
              >
                Publicar Edital
              </button>
              <Link
                to={`/admin/editais/${id}/editar`}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition"
              >
                Editar
              </Link>
              <button
                onClick={handleExcluir}
                disabled={processando}
                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition disabled:opacity-50"
              >
                Excluir
              </button>
            </>
          )}
          {edital.status === "publicado" && (
            <>
              <button
                onClick={handleEncerrar}
                disabled={processando}
                className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 transition disabled:opacity-50"
              >
                Encerrar Edital
              </button>
              <Link
                to={`/admin/inscricoes?edital_id=${id}`}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition"
              >
                Ver Inscrições
              </Link>
              <button
                onClick={() => document.getElementById('anexos-input')?.click()}
                disabled={uploadando}
                className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploadando ? "Enviando..." : "Adicionar Novo Anexo"}
              </button>
            </>
          )}
          {edital.edital_pdf_url && (
            <a
              href={edital.edital_pdf_url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition"
            >
              Ver PDF
            </a>
          )}
        </div>
      </div>

      {/* Estatísticas */}
      {edital.estatisticas && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            Estatísticas de Inscrições
          </h2>
          <div className="grid grid-cols-5 gap-4">
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">
                {edital.estatisticas.total_inscricoes}
              </div>
              <div className="text-sm text-gray-600">Total</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-600">
                {edital.estatisticas.pendentes}
              </div>
              <div className="text-sm text-gray-600">Pendentes</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-blue-600">
                {edital.estatisticas.em_analise}
              </div>
              <div className="text-sm text-gray-600">Em Análise</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600">
                {edital.estatisticas.aprovadas}
              </div>
              <div className="text-sm text-gray-600">Aprovadas</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600">
                {edital.estatisticas.reprovadas}
              </div>
              <div className="text-sm text-gray-600">Reprovadas</div>
            </div>
          </div>
        </div>
      )}

      {/* Informações */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-lg font-bold text-gray-900 mb-4">
          Informações do Edital
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-600">
              Descrição
            </label>
            <p className="mt-1 text-gray-900">{edital.descricao}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600">
                Data de Início
              </label>
              <p className="mt-1 text-gray-900">
                {new Date(edital.data_inicio).toLocaleDateString("pt-BR")}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600">
                Data de Término
              </label>
              <p className="mt-1 text-gray-900">
                {new Date(edital.data_fim).toLocaleDateString("pt-BR")}
              </p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600">
              Template Utilizado
            </label>
            <p className="mt-1 text-gray-900">{edital.template.nome}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600">
              Criado por
            </label>
            <p className="mt-1 text-gray-900">{edital.criado_por.nome}</p>
          </div>
        </div>
      </div>

      {/* Documentos Anexados */}
      {edital.anexos && edital.anexos.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">
            Documentos Anexados
          </h2>
          <div className="space-y-3">
            {edital.anexos.map((anexo) => (
              <div
                key={anexo.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition"
              >
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{anexo.filename}</p>
                  <div className="flex flex-wrap gap-4 mt-1 text-sm text-gray-600">
                    <span>
                      Tamanho: {(anexo.byte_size / 1024 / 1024).toFixed(2)} MB
                    </span>
                    <span>Tipo: {anexo.content_type}</span>
                    {anexo.adicionado_por && (
                      <>
                        <span>•</span>
                        <span className="text-blue-600 font-medium">
                          Adicionado por: {anexo.adicionado_por.nome}
                        </span>
                      </>
                    )}
                    {anexo.adicionado_em && (
                      <>
                        <span>•</span>
                        <span>
                          {new Date(anexo.adicionado_em).toLocaleString("pt-BR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <a
                  href={anexo.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition text-sm font-medium"
                >
                  Visualizar
                </a>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal de Confirmação */}
      {showConfirmModal && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4 z-50 animate-fadeIn"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
          }}
          onClick={() => {
            setShowConfirmModal(false);
            setFilesParaUpload([]);
            const fileInput = document.getElementById('anexos-input') as HTMLInputElement;
            if (fileInput) fileInput.value = '';
          }}
        >
          <div
            className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl animate-scaleIn"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">
              Confirmar Adição de Anexos
            </h3>
            <p className="text-gray-600 mb-3">
              Deseja adicionar {filesParaUpload.length > 1 ? 'os seguintes arquivos' : 'o arquivo'}:
            </p>
            <div className="bg-gray-50 rounded-lg p-4 mb-6 max-h-60 overflow-y-auto border border-gray-200">
              <ul className="space-y-2">
                {filesParaUpload.map((file, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm">
                    <span className="text-blue-600 mt-1">•</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{file.name}</p>
                      <p className="text-xs text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex flex-col-reverse sm:flex-row gap-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setFilesParaUpload([]);
                  const fileInput = document.getElementById('anexos-input') as HTMLInputElement;
                  if (fileInput) fileInput.value = '';
                }}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 active:scale-95 transition-all font-semibold"
                disabled={uploadando}
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  setShowConfirmModal(false);
                  try {
                    setUploadando(true);
                    const response = await editaisAdminService.anexar(
                      parseInt(id!),
                      filesParaUpload
                    );
                    toast.success("Anexos adicionados com sucesso!");
                    setEdital(response.edital);
                    const fileInput = document.getElementById('anexos-input') as HTMLInputElement;
                    if (fileInput) fileInput.value = '';
                  } catch (err) {
                    toast.error("Erro ao adicionar anexos");
                    console.error(err);
                  } finally {
                    setUploadando(false);
                    setFilesParaUpload([]);
                  }
                }}
                disabled={uploadando}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold shadow-md hover:shadow-lg"
              >
                {uploadando ? "Enviando..." : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Input de upload oculto */}
      <input
        id="anexos-input"
        type="file"
        multiple
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) {
            const files = Array.from(e.target.files);
            setFilesParaUpload(files);
            setShowConfirmModal(true);
          }
        }}
        className="hidden"
      />
    </div>
  );
}
